import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AIAgentWebhook, CreateAIAgentWebhookRequest, UpdateAIAgentWebhookRequest } from '@troia-v3/types';
import { toast } from 'sonner';

import { agentsApi } from '../services/agentsApi';

export const useCreateAgentWebhook = (agentId?: string) => {
  const queryClient = useQueryClient();

  return useMutation<AIAgentWebhook, Error, CreateAIAgentWebhookRequest>({
    mutationFn: (data) => {
      if (!agentId) throw new Error('agentId é obrigatório');
      return agentsApi.createWebhook(agentId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      toast.success('Webhook criado com sucesso!');
    },
    onError: (error) => {
      toast.error(`Erro ao criar webhook: ${error.message}`);
    },
  });
};

export const useUpdateAgentWebhook = (agentId?: string) => {
  const queryClient = useQueryClient();

  return useMutation<AIAgentWebhook, Error, { webhookId: string; data: UpdateAIAgentWebhookRequest }>({
    mutationFn: ({ webhookId, data }) => {
      if (!agentId) throw new Error('agentId é obrigatório');
      return agentsApi.updateWebhook(agentId, webhookId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      toast.success('Webhook atualizado com sucesso!');
    },
    onError: (error) => {
      toast.error(`Erro ao atualizar webhook: ${error.message}`);
    },
  });
};

export const useUpdateAgentWebhookStatus = (agentId?: string) => {
  const queryClient = useQueryClient();

  return useMutation<AIAgentWebhook, Error, { webhookId: string; status: 'active' | 'inactive' }>({
    mutationFn: ({ webhookId, status }) => {
      if (!agentId) throw new Error('agentId é obrigatório');
      return agentsApi.updateWebhookStatus(agentId, webhookId, status);
    },
    onSuccess: (webhook) => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      toast.success(webhook.status === 'active' ? 'Webhook ativado!' : 'Webhook desativado!');
    },
    onError: (error) => {
      toast.error(`Erro ao atualizar status do webhook: ${error.message}`);
    },
  });
};

export const useDeleteAgentWebhook = (agentId?: string) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (webhookId) => {
      if (!agentId) throw new Error('agentId é obrigatório');
      return agentsApi.deleteWebhook(agentId, webhookId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      toast.success('Webhook excluído com sucesso!');
    },
    onError: (error) => {
      toast.error(`Erro ao excluir webhook: ${error.message}`);
    },
  });
};
